import { useQuery } from '@tanstack/react-query'
import {
  api,
  type ARSubscriberHistory,
  type ARFailureCategory,
  type ARCardHealth,
} from '../api/client'
import { fmtRelative, publicationName } from '../lib/format'

const HEALTH_TONE: Record<ARCardHealth, string> = {
  healthy: 'text-radar-green',
  transient_decline: 'text-paper-dim',
  chronic_failure: 'text-amber-glow',
  multi_cycle_failure: 'text-radar-red',
}

const HEALTH_LABEL: Record<ARCardHealth, string> = {
  healthy: 'healthy',
  transient_decline: 'transient decline',
  chronic_failure: 'chronic failure (this cycle)',
  multi_cycle_failure: 'multi-cycle failure',
}

const FAILURE_LABELS: Record<ARFailureCategory, string> = {
  invalid_card_closed: 'Invalid card (closed/replaced)',
  invalid_card_expired: 'Card expired',
  invalid_card: 'Invalid card', // legacy
  no_payment_profile: 'No payment profile',
  card_type_unsupported: 'Card type unsupported',
  insufficient_funds: 'Insufficient funds',
  declined: 'Declined',
  avs_mismatch: 'AVS mismatch',
  system_error: 'System error',
  other: 'Other',
}

const STATUS_TONE: Record<string, string> = {
  CHARGED: 'text-radar-green',
  'CARD UPDATED': 'text-radar-cyan',
  'FIRST CHARGE FAILED': 'text-amber-glow',
  'CHARGE FAILED': 'text-amber-glow',
  'RECHARGE FAILED': 'text-amber-glow',
  'THIRD CHARGE FAILED': 'text-radar-red/80',
  'FOURTH CHARGE FAILED': 'text-radar-red/80',
  'FIFTH CHARGE FAILED': 'text-radar-red',
  'FINAL CHARGE FAILED': 'text-radar-red',
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export function ARHistoryTimeline({ subscriberId }: { subscriberId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar-history', subscriberId],
    queryFn: () => api.get<ARSubscriberHistory>(
      `/api/ar-report/subscriber/${subscriberId}/ar-history`,
    ),
    enabled: !!subscriberId,
  })

  if (isLoading) return <div className="label text-paper-trace p-4">Loading AR history…</div>
  if (!data) return null

  const hasData = data.attempts.length > 0 || data.cohort_entries.length > 0

  if (!hasData) {
    return (
      <div className="label text-paper-trace p-4 border border-ink-500 bg-ink-800/40">
        No AR attempt history for this subscriber.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label text-paper-faint">Card health</div>
          <div className={`font-mono text-[18px] ${HEALTH_TONE[data.card_health]}`}>
            {HEALTH_LABEL[data.card_health]}
          </div>
        </div>
        <div className="text-right">
          <div className="label text-paper-faint">Total AR attempts</div>
          <div className="font-mono text-[18px] tabular-nums text-paper">{data.attempts.length}</div>
        </div>
      </div>

      {data.cohort_entries.length > 0 && (
        <div>
          <div className="label text-paper-faint mb-2">Cohort entries</div>
          <div className="space-y-1">
            {data.cohort_entries.map((c, i) => (
              <div key={i} className="text-[12px] font-mono text-paper-dim">
                {c.cohort_month} · {publicationName(c.publication_slug)}
                {c.is_ar ? ' · AR' : ''}
                {c.is_agent ? ' · agent' : ''}
                {c.entry_ar_status ? ` · entered as ${c.entry_ar_status}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="label text-paper-faint mb-2">Attempt timeline (newest first)</div>
        <div className="border border-ink-500 bg-ink-800/40">
          {data.attempts.length === 0 ? (
            <div className="px-4 py-3 label text-paper-trace">No attempts recorded.</div>
          ) : (
            data.attempts.map((a, i) => (
              <div key={i} className="px-4 py-3 border-b border-ink-500/30 last:border-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-mono text-[12px] text-paper-dim">{fmtTime(a.attempt_at)}</div>
                  <div className={`font-mono text-[12px] ${STATUS_TONE[a.status] || 'text-paper'}`}>
                    {a.status}
                  </div>
                </div>
                {a.failure_category && (
                  <div className="text-[11px] text-paper-trace font-mono mt-1">
                    {FAILURE_LABELS[a.failure_category]}
                  </div>
                )}
                {a.details && (
                  <div className="text-[11px] text-paper-dim mt-1 italic">
                    "{a.details}"
                  </div>
                )}
                {a.paid_amount !== null && a.paid_amount > 0 && (
                  <div className="text-[11px] text-radar-green font-mono mt-1">
                    paid: ${a.paid_amount.toFixed(2)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {data.recovery_emails.length > 0 && (
        <div>
          <div className="label text-paper-faint mb-2">Recovery emails sent (last 365 days)</div>
          <div className="border border-ink-500 bg-ink-800/40">
            {data.recovery_emails.map((e) => (
              <div key={e.send_id} className="px-4 py-2 border-b border-ink-500/30 last:border-0 text-[12px] font-mono">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-paper">{e.subject || e.template_slug}</span>
                  <span className="text-paper-trace">{e.sent_at ? fmtRelative(e.sent_at) : '—'}</span>
                </div>
                <div className="text-[11px] text-paper-trace mt-0.5">
                  template: {e.template_slug} · status: {e.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
